import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { auth } from '../services/firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  User,
} from 'firebase/auth';
import { UserProfile } from '../types';

interface AuthContextType {
  user: UserProfile | null;
  firebaseUser: User | null;
  loading: boolean;
  authError: string;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);
export const useAuth = () => useContext(AuthContext);

const ADMIN_EMAILS = [
  'soihibaliousseni@gmail.com',
  'demo@contenuia.com',
  'ali@chanbeia.com',
];

/** Traduit les codes d'erreur Firebase en messages lisibles pour l'utilisateur. */
function friendlyAuthError(error: any): string {
  const code = error?.code || '';
  const map: Record<string, string> = {
    'auth/invalid-email': "L'adresse email n'est pas valide.",
    'auth/user-disabled': 'Ce compte a été désactivé. Contactez le support.',
    'auth/user-not-found': "Aucun compte ne correspond à cette adresse email.",
    'auth/wrong-password': 'Mot de passe incorrect.',
    'auth/invalid-credential': 'Email ou mot de passe incorrect.',
    'auth/email-already-in-use': 'Un compte existe déjà avec cette adresse email.',
    'auth/weak-password': 'Le mot de passe doit contenir au moins 6 caractères.',
    'auth/missing-password': 'Veuillez saisir un mot de passe.',
    'auth/too-many-requests': 'Trop de tentatives. Réessayez dans quelques minutes.',
    'auth/network-request-failed': 'Problème de connexion. Vérifiez votre réseau.',
    'auth/operation-not-allowed': "Cette méthode de connexion n'est pas activée.",
    'auth/popup-closed-by-user': 'La fenêtre de connexion a été fermée.',
  };
  if (map[code]) return map[code];
  if (error?.message?.includes('network')) return 'Problème de connexion. Vérifiez votre réseau.';
  return "Une erreur est survenue. Réessayez ou contactez le support.";
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    let unsub: (() => void) | undefined;
    // Filet de sécurité : ne jamais rester bloqué sur "Chargement..."
    const safety = setTimeout(() => setLoading(false), 8000);

    try {
      unsub = onAuthStateChanged(
        auth,
        (fbUser) => {
          try {
            if (fbUser) {
              setFirebaseUser(fbUser);
              const isAdmin = ADMIN_EMAILS.includes((fbUser.email || '').toLowerCase());
              setUser({
                uid: fbUser.uid,
                email: fbUser.email || '',
                name: fbUser.displayName || fbUser.email?.split('@')[0] || 'Utilisateur',
                plan: isAdmin ? 'agency' : 'free',
                quotaUsed: 0,
                quotaMax: isAdmin ? 99999 : 10,
                isAdmin,
                isUnlimited: isAdmin,
                createdAt: fbUser.metadata?.creationTime || '',
              });
            } else {
              setFirebaseUser(null);
              setUser(null);
            }
          } catch (e) {
            console.error('[Auth] Erreur traitement utilisateur:', e);
            setUser(null);
          } finally {
            clearTimeout(safety);
            setLoading(false);
          }
        },
        (error) => {
          // Callback d'erreur de onAuthStateChanged
          console.error('[Auth] onAuthStateChanged error:', error);
          setAuthError(friendlyAuthError(error));
          clearTimeout(safety);
          setLoading(false);
        }
      );
    } catch (e) {
      console.error('[Auth] Impossible de démarrer onAuthStateChanged:', e);
      setAuthError(friendlyAuthError(e));
      clearTimeout(safety);
      setLoading(false);
    }

    return () => {
      clearTimeout(safety);
      if (unsub) unsub();
    };
  }, []);

  const login = async (email: string, password: string) => {
    setAuthError('');
    if (!email?.trim()) throw new Error('Veuillez saisir votre adresse email.');
    if (!password) throw new Error('Veuillez saisir votre mot de passe.');
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (e: any) {
      const msg = friendlyAuthError(e);
      setAuthError(msg);
      throw new Error(msg);
    }
  };

  const register = async (email: string, password: string, name: string) => {
    setAuthError('');
    if (!email?.trim()) throw new Error('Veuillez saisir votre adresse email.');
    if (!password || password.length < 6) throw new Error('Le mot de passe doit contenir au moins 6 caractères.');
    try {
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      if (name?.trim() && cred.user) {
        try {
          await updateProfile(cred.user, { displayName: name.trim() });
        } catch (e) {
          console.warn('[Auth] displayName non enregistré:', e);
        }
      }
    } catch (e: any) {
      const msg = friendlyAuthError(e);
      setAuthError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    setAuthError('');
    try {
      await signOut(auth);
      // Nettoyer les tokens tiers stockés localement
      ['yt_access_token', 'yt_token_expiry', 'yt_refresh_token', 'tiktok_access_token'].forEach(k =>
        localStorage.removeItem(k)
      );
    } catch (e: any) {
      const msg = friendlyAuthError(e);
      setAuthError(msg);
      throw new Error(msg);
    }
  };

  return (
    <AuthContext.Provider value={{ user, firebaseUser, loading, authError, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
